// frontend/src/utils/getImageColors.js

// Корпоративные цвета
const CORPORATE_COLORS = ['#3c6e71', '#284b63'];

// Функция для преобразования RGB в HEX
const rgbToHex = (r, g, b) => {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

// Функция для получения яркости цвета
const getBrightness = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

// Функция для затемнения цвета
const darkenColor = (hex, percent) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const newR = Math.max(0, Math.min(255, r + (r * percent / 100)));
  const newG = Math.max(0, Math.min(255, g + (g * percent / 100)));
  const newB = Math.max(0, Math.min(255, b + (b * percent / 100)));

  return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
};

export const getDominantColors = async (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const size = 100;
      canvas.width = size;
      canvas.height = size;

      ctx.drawImage(img, 0, 0, size, size);

      const imageData = ctx.getImageData(0, 0, size, size);
      const data = imageData.data;

      const colorMap = new Map();

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        if (a < 128) continue;
        if (r + g + b < 60) continue;
        if (r > 240 && g > 240 && b > 240) continue;

        const roundedR = Math.round(r / 15) * 15;
        const roundedG = Math.round(g / 15) * 15;
        const roundedB = Math.round(b / 15) * 15;

        const hex = rgbToHex(roundedR, roundedG, roundedB);
        colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
      }

      let sortedColors = Array.from(colorMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([hex]) => hex);

      if (sortedColors.length === 0) {
        resolve(CORPORATE_COLORS[0]);
        return;
      }

      // Берем самый частотный цвет
      let mainColor = sortedColors[0];

      // Анализируем яркость
      const brightness = getBrightness(mainColor);

      // Если цвет слишком светлый — затемняем
      if (brightness > 180) {
        mainColor = darkenColor(mainColor, -35);
      }

      resolve(mainColor);
    };

    img.onerror = () => {
      reject(new Error('Не удалось загрузить изображение'));
    };
  });
};

export const getHeaderGradient = async (imageUrl) => {
  try {
    const mainColor = await getDominantColors(imageUrl);

    // 🔥 ПРОСТОЙ ГРАДИЕНТ: от цвета аватарки к корпоративному зеленому
    // Используем легкую диагональ (120deg) для плавности
    const gradient = `
      linear-gradient(
        120deg,
        ${mainColor} 0%,
        ${darkenColor(mainColor, -20)} 30%,
        ${CORPORATE_COLORS[0]} 70%,
        ${CORPORATE_COLORS[1]} 100%
      )
    `;

    // Добавляем прозрачность для читаемости текста (0.85)
    const transparentGradient = gradient
      .replace(/#([A-Fa-f0-9]{6})/g, (match) => {
        const r = parseInt(match.slice(1, 3), 16);
        const g = parseInt(match.slice(3, 5), 16);
        const b = parseInt(match.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.85)`;
      });

    return transparentGradient;
  } catch (error) {
    console.warn('Не удалось извлечь цвета, используем стандартный градиент');
    return `linear-gradient(120deg, rgba(60, 110, 113, 0.85) 0%, rgba(40, 75, 99, 0.85) 50%, #284b63 100%)`;
  }
};